// @flow
import React from "react";
import {compose, withProps, lifecycle, withHandlers, withPropsOnChange, pure} from "recompose";
import {
    withScriptjs,
    withGoogleMap,
    GoogleMap,
} from "react-google-maps";
import OriginMarker from "./Markers/OriginMarker";
import DestMarker from "./Markers/DestMarker";
import DropoffMarker from "./Markers/DropoffMarker";
import {bindActionCreators} from "redux";
import {connect} from "react-redux";
import {styles} from "../../config";
import VanMarker from "./Markers/VanMarker";
import RiderMarker from "./Markers/RiderMarker";
import ServicePolygon from "./ServicePolygon/ServicePolygon";
import {debouncedLatLngGeocoder, getLatLngLiteralFromLatLng, isLatLngInListOfPolygons} from "../../Utils/MapUtils";
import throttle from "lodash/throttle";
import {millisecondsPerFrame} from "../../Utils/AppUtils";
import RideWalkingMap from "./WalkingMap/RideWalkingMap";
import ProposalWalkingMap from "./WalkingMap/ProposalWalkingMap";
import PickupMarkerSimple from "./Markers/PickupMarkerSimple";
import PickupMarkerSpot from "./Markers/PickupMarkerSpot";
import PickupMarkerETA from "./Markers/PickupMarkerETA";
import CurrentRequestSelectors from "../../Store/Ducks/CurrentRequest/selectors";
import ServiceConfigSelectors from "../../Store/Ducks/ServiceConfig/selectors";
import MapDataSelectors from "../../Store/Ducks/MapData/selectors";
import Screens from "../../Store/Views/screens";
import CurrentRequestOperations from "../../Store/Ducks/CurrentRequest/operations";
import MapDataOperations from "../../Store/Ducks/MapData/operations";
import type {LatLngLiteral} from "../../Utils/MapUtils";
import {topHeaderBarHeight} from "../TopBar/TopBarStyle";
import { GOOGLE_AUTH_TOKEN } from '../../config';

const MAP_CENTER_UPDATES_PER_SECOND = 1;

const mapStateToProps = (state, props) => {
    return {
        originLat: CurrentRequestSelectors.getOriginLat(state),
        originLng: CurrentRequestSelectors.getOriginLng(state),
        destLat: CurrentRequestSelectors.getDestLat(state),
        destLng: CurrentRequestSelectors.getDestLng(state),
        currentRequestReady: CurrentRequestSelectors.currentRequestReady(state),
        visualServicePolygonPaths: ServiceConfigSelectors.getVisualServicePolygonPaths(state),
        isSelectingOrigin: Screens.shouldShowSelectOrigin(state),
        isSelectingDest: Screens.shouldShowSelectDest(state),
        isDraggable: CurrentRequestSelectors.isFreeSelectionDragging(state),
        isMapCenterInZone: MapDataSelectors.isMapCenterInZone(state),

        latLngToPanTo: MapDataSelectors.getLatLngToPanTo(state),
        boundsToFit: MapDataSelectors.getBoundsToFit(state),
    };
};

const mapActionsToProps = (dispatch, props) => {
    return bindActionCreators(
        {
            onUpdateOriginAddress: CurrentRequestOperations.updateOriginAddress,
            onUpdateDestAddress: CurrentRequestOperations.updateDestAddress,
            onMapLoaded: MapDataOperations.mapFinishedLoading,
            onUpdateMapInServicePolygon: MapDataOperations.updateMapInServicePolygon,
            onUpdateMapCenter: MapDataOperations.updateMapCenter,
        },
        dispatch);
};

const FIT_BOUNDS_PADDING = {
    top: 200,
    bottom: 150,
    left: 20,
    right: 20,
};


const Map = compose(
    connect(mapStateToProps, mapActionsToProps),
    withProps((props) => ({
        googleMapURL:
            `https://maps.googleapis.com/maps/api/js?v=3&key=${GOOGLE_AUTH_TOKEN}&libraries=places`,
        loadingElement: <div style={{...props.style, height: `100%`}}/>,
        containerElement: <div style={{
            ...props.style,
            height: `calc(100% - ${topHeaderBarHeight})`,
            top: topHeaderBarHeight,
            width: `100%`,
            position: `fixed`,
            zIndex: `0`,
        }}/>,
        mapElement: <div style={{...props.style, height: `100%`}}/>,
    })),
    pure,
    withHandlers((props) => {
        const refs: {
            map: window.google.map,
            debouncedGeocodeLatlngOrigin: (address: string) => mixed,
            debouncedGeocodeLatlngDest: (address: string) => mixed,
        } = {
            map: undefined,
            debouncedGeocodeLatlngOrigin: debouncedLatLngGeocoder(props.onUpdateOriginAddress),
            debouncedGeocodeLatlngDest: debouncedLatLngGeocoder(props.onUpdateDestAddress),
        };
        const throttledOnUpdateMapCenter = throttle(props.onUpdateMapCenter, 1000 / MAP_CENTER_UPDATES_PER_SECOND);
        const geocodeLatlng = (props, center) => {
            if (props.isSelectingOrigin) {
                refs.debouncedGeocodeLatlngOrigin(center);
            } else if (props.isSelectingDest) {
                refs.debouncedGeocodeLatlngDest(center);
            }
        };

        const centerChangedHandler = (props, map) => {
            let center = map.getCenter();
            if (props.visualServicePolygonPaths && props.visualServicePolygonPaths.length > 0) {
                const inZone = isLatLngInListOfPolygons(getLatLngLiteralFromLatLng(center), props.visualServicePolygonPaths);
                if ((inZone ? 1 : 0) ^ props.isMapCenterInZone) {
                    // dispatch an update only when there was an actual change to improve performance
                    props.onUpdateMapInServicePolygon(inZone);
                }
            }
            geocodeLatlng(props, center);
            throttledOnUpdateMapCenter(center);
        };

        const throttledCenterChangedHandler = throttle(centerChangedHandler, millisecondsPerFrame);

        return {
            onMapMounted: props => map => {
                refs.map = map;
                props.onMapLoaded();
                centerChangedHandler(props, map);
            },
            panToLatLng: props => (latlng: window.google.maps.LatLng) => {
                refs.map.panTo(latlng);
            },
            fitBoundsFromLatLngArray: props => (latlngList: Array<LatLngLiteral>) => {
                let bounds = new window.google.maps.LatLngBounds();
                if (latlngList && latlngList.length > 0) {
                    latlngList.forEach((latlng) => {
                        bounds.extend(latlng);
                    });
                    refs.map.fitBounds(bounds, FIT_BOUNDS_PADDING);
                }
            },
            onCenterChangedHandler: props => () => {
                if (refs.map) {
                    throttledCenterChangedHandler(props, refs.map);
                }
            }
        };
    }),
    withPropsOnChange(['visualServicePolygonPaths'], props => {
        if (props.visualServicePolygonPaths) {
            props.onCenterChangedHandler();
        }
        return {};
    }),
    withScriptjs,
    withGoogleMap,
    lifecycle({
        shouldComponentUpdate(nextProps, nextState) {
            //TODO(Oded): find a nicer way to do this
            if (nextProps && (nextProps.latLngToPanTo || nextProps.boundsToFit)) {
                nextProps.latLngToPanTo && this.props.panToLatLng(nextProps.latLngToPanTo);
                nextProps.boundsToFit && this.props.fitBoundsFromLatLngArray(nextProps.boundsToFit);
            }
            return true;
        }
    }),
)(props => {
    if (!props.currentRequestReady) {
        return null;
    }
    const polygonPaths = props.visualServicePolygonPaths;
    const defaultCenter = props.latLngToPanTo ? props.latLngToPanTo :
        (props.isSelectingOrigin ? {lat: props.originLat, lng: props.originLng} :
            {lat: props.destLat, lng: props.destLng});
    return (
        <GoogleMap
            {...props}
            ref={props.onMapMounted}
            defaultCenter={defaultCenter}
            defaultZoom={14}
            options={{
                mapTypeId: "roadmap",
                disableDefaultUI: true,
                mapTypeControl: false,
                styles: styles.googleMapStyles,
                draggable: props.isDraggable,
                maxZoom: 17,
            }}
            onCenterChanged={function () {
                props.onCenterChangedHandler();
            }}
        >
            <React.Fragment key={"mapMarkersAndPolygons"}>
                {polygonPaths && (<ServicePolygon key="servicePolygon" polygonPaths={polygonPaths}/>)}
                <RideWalkingMap key="rideWalkingMap"/>
                <ProposalWalkingMap key="proposalWalkingMap"/>
                <OriginMarker key="originMarker"/>
                <DestMarker key="destMarker"/>
                <PickupMarkerSimple key="pickupMarkerSimple"/>
                <PickupMarkerSpot key="pickupMarkerSpot"/>
                <PickupMarkerETA key="pickupMarkerETA"/>
                <DropoffMarker key="dropoffMarker"/>
                <VanMarker key="vanMarker"/>
                <RiderMarker key="riderMarker"/>
            </React.Fragment>
        </GoogleMap>
    );
});

export default Map;



// WEBPACK FOOTER //
// ./src/Components/Map/Map.js